/* eslint-disable max-len */
import React, {
  useCallback, useEffect, useRef, useState
} from 'react';
import axios from 'axios';
import {
  getCookie, setCookie, decodeJwt, handleLogoutRedirect
} from '../../utilities/general';
import {
  BASE_API_URL, endpoints, ACCESS_TOKEN_AGE, REFRESH_TOKEN_AGE
} from '../../constants';

const IDLE_TIMEOUT_MS = 15 * 60 * 1000;
const PROACTIVE_REFRESH_BEFORE_S = 60; // refresh 60s before expiry
const WARNING_BEFORE_EXPIRY_S = 30; // only warn if refresh itself fails
const COUNTDOWN_INTERVAL_MS = 1000;
const CHECK_INTERVAL_MS = 30000;

async function doRefresh() {
  const refreshToken = getCookie('_rtk');
  if (!refreshToken) return false;

  try {
    const response = await axios.post(
      `${BASE_API_URL}${endpoints.accounts.refresh}`,
      { refreshToken },
      { withCredentials: true }
    );

    const { loginToken, refreshToken: newRefreshToken } = response.data;
    if (!loginToken) return false;

    setCookie('_tk', loginToken, ACCESS_TOKEN_AGE);
    if (newRefreshToken) {
      setCookie('_rtk', newRefreshToken, REFRESH_TOKEN_AGE);
      const rtkExp = Math.floor(Date.now() / 1000) + REFRESH_TOKEN_AGE;
      setCookie('_rtk_exp', String(rtkExp), REFRESH_TOKEN_AGE);
    }
    return true;
  } catch {
    return false;
  }
}

function SessionManager() {
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [reason, setReason] = useState('');

  const lastActivityRef = useRef(Date.now());
  const warningTimerRef = useRef(null);
  const countdownRef = useRef(null);
  const checkIntervalRef = useRef(null);
  const isRefreshingRef = useRef(false);

  const resetActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  const performLogout = useCallback(() => {
    clearInterval(countdownRef.current);
    clearInterval(checkIntervalRef.current);
    setShowWarning(false);
    handleLogoutRedirect();
  }, []);

  const startCountdown = useCallback((seconds, logoutReason) => {
    if (showWarning) return;
    setReason(logoutReason);
    setCountdown(seconds);
    setShowWarning(true);

    clearInterval(countdownRef.current);
    let remaining = seconds;
    countdownRef.current = setInterval(() => {
      remaining -= 1;
      setCountdown(remaining);
      if (remaining <= 0) {
        performLogout();
      }
    }, COUNTDOWN_INTERVAL_MS);
  }, [showWarning, performLogout]);

  const dismissWarning = useCallback(() => {
    clearInterval(countdownRef.current);
    setShowWarning(false);
    setCountdown(0);
    resetActivity();
  }, [resetActivity]);

  const checkSession = useCallback(async () => {
    const token = getCookie('_tk');

    if (!token) {
      // No access token — try to refresh silently before giving up
      if (!isRefreshingRef.current) {
        isRefreshingRef.current = true;
        const ok = await doRefresh();
        isRefreshingRef.current = false;
        if (!ok) performLogout();
      }
      return;
    }

    const decoded = decodeJwt(token);
    if (!decoded || !decoded.exp) {
      performLogout();
      return;
    }

    const now = Math.floor(Date.now() / 1000);
    const timeUntilExpiry = decoded.exp - now;

    if (timeUntilExpiry <= 0) {
      // Token already expired — try refresh
      if (!isRefreshingRef.current) {
        isRefreshingRef.current = true;
        const ok = await doRefresh();
        isRefreshingRef.current = false;
        if (!ok) performLogout();
      }
      return;
    }

    if (timeUntilExpiry <= PROACTIVE_REFRESH_BEFORE_S && !isRefreshingRef.current) {
      // Proactively refresh before expiry
      isRefreshingRef.current = true;
      const ok = await doRefresh();
      isRefreshingRef.current = false;
      if (!ok && timeUntilExpiry <= WARNING_BEFORE_EXPIRY_S && !showWarning) {
        // Refresh failed and expiry is imminent — warn user
        startCountdown(timeUntilExpiry, 'Your session is about to expire and could not be renewed.');
      }
      return;
    }

    const idleMs = Date.now() - lastActivityRef.current;
    if (idleMs >= IDLE_TIMEOUT_MS && !showWarning) {
      const idleCountdown = Math.min(60, timeUntilExpiry);
      startCountdown(idleCountdown, 'You have been inactive for too long.');
    }
  }, [showWarning, performLogout, startCountdown]);

  useEffect(() => {
    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    events.forEach((e) => window.addEventListener(e, resetActivity, { passive: true }));

    checkIntervalRef.current = setInterval(checkSession, CHECK_INTERVAL_MS);
    checkSession();

    return () => {
      events.forEach((e) => window.removeEventListener(e, resetActivity));
      clearInterval(checkIntervalRef.current);
      clearInterval(countdownRef.current);
      clearTimeout(warningTimerRef.current);
    };
  }, [resetActivity, checkSession]);

  if (!showWarning) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        zIndex: 99999
      }}
    >
      <div
        style={{
          backgroundColor: '#fff',
          borderRadius: '12px',
          padding: '32px 40px',
          maxWidth: '420px',
          width: '90%',
          textAlign: 'center',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
        }}
      >
        <div
          style={{
            width: '56px',
            height: '56px',
            borderRadius: '50%',
            backgroundColor: '#FEF3C7',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px'
          }}
        >
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#D97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        </div>
        <h2 style={{
          margin: '0 0 8px', fontSize: '20px', fontWeight: 600, color: '#111827'
        }}
        >
          Session Warning
        </h2>
        <p style={{
          margin: '0 0 20px', fontSize: '14px', color: '#6B7280', lineHeight: 1.5
        }}
        >
          {reason}
          {' '}
          You will be logged out in
          {' '}
          <strong style={{ color: '#DC2626', fontSize: '18px' }}>{countdown}</strong>
          {' '}
          second
          {countdown !== 1 ? 's' : ''}
          .
        </p>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
          <button
            type="button"
            onClick={dismissWarning}
            style={{
              padding: '10px 24px',
              borderRadius: '8px',
              border: 'none',
              backgroundColor: '#4F46E5',
              color: '#fff',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer'
            }}
          >
            Stay Logged In
          </button>
          <button
            type="button"
            onClick={performLogout}
            style={{
              padding: '10px 24px',
              borderRadius: '8px',
              border: '1px solid #D1D5DB',
              backgroundColor: '#fff',
              color: '#374151',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer'
            }}
          >
            Logout Now
          </button>
        </div>
      </div>
    </div>
  );
}

export default SessionManager;
